import { zodResolver } from '@hookform/resolvers/zod'
import {
    Building2,
    Plus,
    Edit,
    Trash2,
    ToggleLeft,
    ToggleRight,
    MapPin,
    Phone,
    Mail,
    Check,
    AlertCircle,
    Truck,
} from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import z from 'zod'

import { ConfirmationModal } from '../components/ui/ConfirmationModal'
import { FormError } from '../components/ui/FormError'
import { GlobalFilterBar } from '../components/ui/GlobalFilterBar'
import { Badge } from '../components/ui/badge'
import { Button } from '../components/ui/button'
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from '../components/ui/dialog'
import { GlobalPaginator } from '../components/ui/global-paginator'
import { Input } from '../components/ui/input'
import { TableSkeleton } from '../components/ui/table-skeleton'
import { sucursalService, Sucursal } from '../services/sucursal.service'
import { useSessionStore } from '../store/auth/auth.store'
import { ShippingCoverageModal } from '../components/sucursals/ShippingCoverageModal'

import { Spinner } from '@/app/lib/loader/loader'

// ─── Esquemas de validación ─────────────────────────────────────────────────

const sucursalSchema = z.object({
    name: z.string().min(1, 'El nombre es requerido'),
    address: z.string().optional(),
    phone_number: z.string().optional(),
    email: z.union([z.literal(''), z.string().email('Debes ingresar un correo válido')]).optional(),
    weekday_hours: z.string().optional(),
    weekend_hours: z.string().optional(),
    saturday_closed: z.boolean(),
    sunday_closed: z.boolean(),
    is_active: z.boolean(),
})

type SucursalFormValidate = z.infer<typeof sucursalSchema>

// ─── Component ────────────────────────────────────────────────────────────────

export function Sucursals() {
    const [sucursales, setSucursales] = useState<Sucursal[]>([])
    const [isLoading, setIsLoading] = useState(true)
    const [searchQuery, setSearchQuery] = useState('')

    const sessionStore = useSessionStore()
    const companyId = sessionStore.user?.company_id
    const canCreateSucursal = sessionStore.canAction('sucursals.sucursal.create')
    const canEditSucursal = sessionStore.canAction('sucursals.sucursal.edit')
    const canDeleteSucursal = sessionStore.canAction('sucursals.sucursal.delete')
    const canStatusSucursal = sessionStore.canAction('sucursals.sucursal.status')
    const canViewShipping = sessionStore.canAction('sucursals.shipping.view')

    // Paginación
    const [pagination, setPagination] = useState({
        currentPage: 1,
        total: 0,
        perPage: 10,
        lastPage: 1,
        from: 0,
        to: 0,
    })


    // Modals state
    const [showModal, setShowModal] = useState(false)
    const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null)
    const [statusConfirmId, setStatusConfirmId] = useState<number | null>(null)
    const [editingId, setEditingId] = useState<number | null>(null)
    const [shippingCoverageSucursal, setShippingCoverageSucursal] = useState<Sucursal | null>(null)
    const [weekdayOpen, setWeekdayOpen] = useState('')
    const [weekdayClose, setWeekdayClose] = useState('')
    const [weekendOpen, setWeekendOpen] = useState('')
    const [weekendClose, setWeekendClose] = useState('')

    const {
        register,
        handleSubmit,
        setValue,
        watch,
        reset,
        formState: { errors, isSubmitting }
    } = useForm<SucursalFormValidate>({
        resolver: zodResolver(sucursalSchema),
        defaultValues: {
            name: '',
            address: '',
            phone_number: '',
            email: '',
            weekday_hours: '',
            weekend_hours: '',
            saturday_closed: false,
            sunday_closed: false,
            is_active: true,
        }
    })

    const isActiveValue = watch('is_active')
    const saturdayClosed = watch('saturday_closed')
    const sundayClosed = watch('sunday_closed')

    // ─── Handlers ───────────────────────────────────────────────────────────────

    const fetchSucursales = useCallback(async (page = 1, search = searchQuery) => {
        setIsLoading(true)
        if (!companyId) {
            setIsLoading(false)
            return
        }

        try {
            const res = await sucursalService.getSucursales({
                company_id: companyId,
                page,
                per_page: pagination.perPage,
                search,
            })
            const paginator = res.data

            setSucursales(paginator.data)

            setPagination({
                currentPage: paginator.current_page,
                total: paginator.total,
                perPage: paginator.per_page,
                lastPage: paginator.last_page,
                from: paginator.from,
                to: paginator.to,
            })
        } catch {
            toast.error('Error al cargar las sucursales')
        } finally {
            setIsLoading(false)
        }
    }, [companyId, pagination.perPage, searchQuery])

    useEffect(() => {
        fetchSucursales(pagination.currentPage, searchQuery)
    }, [fetchSucursales, pagination.currentPage, searchQuery])




    const openCreateModal = () => {
        setEditingId(null)
        reset({
            name: '',
            address: '',
            phone_number: '',
            email: '',
            weekday_hours: '',
            weekend_hours: '',
            saturday_closed: false,
            sunday_closed: false,
            is_active: true,
        })
        setWeekdayOpen('')
        setWeekdayClose('')
        setWeekendOpen('')
        setWeekendClose('')
        setShowModal(true)
    }

    const openEditModal = (sucursal: Sucursal) => {
        setEditingId(sucursal.id)

        const [weekdayOpenValue = '', weekdayCloseValue = ''] =
            sucursal.weekday_hours?.split(' - ') ?? []

        const [weekendOpenValue = '', weekendCloseValue = ''] =
            sucursal.weekend_hours?.split(' - ') ?? []

        setWeekdayOpen(weekdayOpenValue)
        setWeekdayClose(weekdayCloseValue)
        setWeekendOpen(weekendOpenValue)
        setWeekendClose(weekendCloseValue)

        reset({
            name: sucursal.name,
            address: sucursal.address || '',
            phone_number: sucursal.phone_number || '',
            email: sucursal.email || '',
            weekday_hours: sucursal.weekday_hours || '',
            weekend_hours: sucursal.weekend_hours || '',
            saturday_closed: sucursal.saturday_closed,
            sunday_closed: sucursal.sunday_closed,
            is_active: sucursal.is_active,
        })

        setShowModal(true)
    }

    const handleCloseModal = () => {
        setShowModal(false)
        setEditingId(null)
        setWeekdayOpen('')
        setWeekdayClose('')
        setWeekendOpen('')
        setWeekendClose('')
        reset()
    }

    const onSubmit = async (data: SucursalFormValidate) => {
        try {
        const payload = {
        ...data,
        address: data.address || null,
        phone_number: data.phone_number || null,
        email: data.email || null,
        weekday_hours:
            weekdayOpen && weekdayClose
                ? `${weekdayOpen} - ${weekdayClose}`
                : null,
        weekend_hours:
            weekendOpen && weekendClose
                ? `${weekendOpen} - ${weekendClose}`
                : null,
    }

            if (editingId) {
                await sucursalService.updateSucursal(editingId, payload)
                toast.success('Sucursal actualizada exitosamente')
            } else {
                if (!companyId) {
                    throw new Error('No fue posible identificar la empresa activa.')
                }
                await sucursalService.createSucursal({ company_id: companyId, ...payload })
                toast.success('Sucursal creada exitosamente')
            }
            handleCloseModal()
            fetchSucursales()
        } catch {
            toast.error('Ocurrió un error al guardar la sucursal')
        }
    }

    const handleDelete = async (id: number) => {
        try {
            await sucursalService.deleteSucursal(id)

            toast.success('Sucursal eliminada exitosamente')
            setDeleteConfirmId(null)
            fetchSucursales()

        } catch (error: unknown) {

            const message =
                (error as { response?: { data?: { message?: string } } })?.response?.data?.message ??
                'Error al eliminar la sucursal'

            toast.error(message)
        }
    }

    const executeChangeStatus = async () => {
        if (!statusConfirmId) return

        const sucursal = sucursales.find(
            (item) => item.id === statusConfirmId
        )

        if (!sucursal) return

        try {
            await sucursalService.updateSucursal(statusConfirmId, {
                is_active: !sucursal.is_active,
            })

            toast.success(
                `Sucursal ${sucursal.is_active ? 'desactivada' : 'activada'}`
            )

            fetchSucursales()
        } catch {
            toast.error('Error al cambiar el estado de la sucursal')
        } finally {
            setStatusConfirmId(null)
        }
    }

    // ─── Render ──────────────────────────────────────────────────────────────────

    return (

        <div className="space-y-6 p-6">
            {/* Header & Filters */}
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div>
                    <h1 className="text-2xl font-semibold text-foreground">Sucursales</h1>
                    <p className="mt-1 text-sm text-muted-foreground">
                        Administra las sucursales y puntos de venta de la empresa.
                    </p>
                </div>
                {canCreateSucursal && (
                    <Button
                        onClick={openCreateModal}
                        className="bg-primary hover:bg-primary/90 w-full sm:w-auto">
                        <Plus className="mr-2 h-4 w-4" />
                        Nueva Sucursal
                    </Button>
                )}
            </div>
            <GlobalFilterBar
                show={['search']}
                values={{
                    search: searchQuery,
                }}
                onChange={(changes) => {
                    if (changes.search !== undefined) {
                        setSearchQuery(changes.search)
                        setPagination(prev => ({
                            ...prev,
                            currentPage: 1,
                        }))
                    }
                }}
                customOptions={{
                    searchPlaceholder: 'Buscar sucursal...',
                }}
            />
            {/* Table */}
            {isLoading ? (
                <TableSkeleton
                    headers={['Sucursal', 'Contacto', 'Fecha Registro', 'Estado', 'Acciones']}
                    columns={['avatar-text', 'text-two-lines', 'text', 'badge', 'actions']}
                    rows={5}
                />
            ) : (
                <div className="overflow-hidden rounded-lg border border-border bg-table-bg shadow-sm">
                    <div className="overflow-x-auto">
                        <table className="w-full whitespace-nowrap">
                            <thead className="border-b border-border bg-table-header-bg">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-table-header-foreground">
                                        Sucursal
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-table-header-foreground">
                                        Contacto
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-table-header-foreground">
                                        Fecha Registro
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-table-header-foreground">
                                        Estado
                                    </th>
                                    <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-table-header-foreground">
                                        Acciones
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-border">
                                {sucursales.length > 0 ? (
                                    sucursales.map((sucursal) => (
                                        <tr key={sucursal.id} className="transition-colors hover:bg-table-row-hover">
                                            <td className="px-6 py-4">
                                                <div className="flex items-center gap-3">
                                                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                                                        <Building2 className="h-5 w-5" />
                                                    </div>
                                                    <div>
                                                        <p className="font-medium text-table-foreground">{sucursal.name}</p>
                                                        <p className="flex items-center gap-1 text-sm text-table-foreground">
                                                            <MapPin className="h-3 w-3" />
                                                            {sucursal.address || 'Sin dirección'}
                                                        </p>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="flex flex-col gap-1 text-sm text-table-foreground">
                                                    {sucursal.email && (
                                                        <span className="flex items-center gap-1.5">
                                                            <Mail className="h-3.5 w-3.5 text-table-foreground/70" />
                                                            {sucursal.email}
                                                        </span>
                                                    )}
                                                    {sucursal.phone_number && (
                                                        <span className="flex items-center gap-1.5">
                                                            <Phone className="h-3.5 w-3.5 text-table-foreground/70" />
                                                            {sucursal.phone_number}
                                                        </span>
                                                    )}
                                                    {!sucursal.email && !sucursal.phone_number && (
                                                        <span className="text-table-foreground/70 italic">Sin contacto</span>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 text-sm text-table-foreground">
                                                {sucursal.created_at ? sucursal.created_at.split('T')[0] : ''}
                                            </td>
                                            <td className="px-6 py-4">
                                                <Badge
                                                    className={
                                                        sucursal.is_active
                                                            ? 'bg-[#DCFCE6] text-[#008438]'
                                                            : 'bg-[#FFE2E2] text-[#C10008]'
                                                    }
                                                >
                                                    {sucursal.is_active ? 'Activa' : 'Inactiva'}
                                                </Badge>
                                            </td>
                                            <td className="px-6 py-4 text-right">
                                                <div className="flex items-center justify-end gap-2">
                                                    {canViewShipping && (
                                                        <button
                                                            onClick={() => setShippingCoverageSucursal(sucursal)}
                                                            title="Gestionar coberturas de envío"
                                                            className="rounded p-1.5 text-primary transition-colors hover:bg-primary/10"
                                                        >
                                                            <Truck className="h-4 w-4" />
                                                        </button>
                                                    )}
                                                    {canStatusSucursal && (
                                                        <button
                                                            onClick={() => setStatusConfirmId(sucursal.id)}
                                                            title={sucursal.is_active ? 'Desactivar' : 'Activar'}
                                                            className={`rounded p-1.5 transition-colors ${sucursal.is_active
                                                                ? 'text-primary hover:bg-primary/10'
                                                                : 'text-table-foreground hover:bg-muted'
                                                                }`}
                                                        >
                                                            {sucursal.is_active ? (
                                                                <ToggleRight className="h-5 w-5" />
                                                            ) : (
                                                                <ToggleLeft className="h-5 w-5" />
                                                            )}
                                                        </button>
                                                    )}
                                                    {canEditSucursal && (
                                                        <button
                                                            onClick={() => openEditModal(sucursal)}
                                                            title="Editar"
                                                            className="rounded p-1.5 text-table-foreground transition-colors hover:bg-muted"
                                                        >
                                                            <Edit className="h-4 w-4" />
                                                        </button>
                                                    )}
                                                    {canDeleteSucursal && (
                                                        <button
                                                            onClick={() => setDeleteConfirmId(sucursal.id)}
                                                            title="Eliminar"
                                                            className="rounded p-1.5 text-destructive transition-colors hover:bg-destructive/10"
                                                        >
                                                            <Trash2 className="h-4 w-4" />
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan={5} className="px-6 py-12 text-center">
                                            <Building2 className="mx-auto mb-3 h-8 w-8 text-table-foreground/30" />
                                            <p className="text-table-foreground">No se encontraron sucursales</p>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                    {/* Controles de Paginacion */}
                    {sucursales.length > 0 && (
                        <GlobalPaginator
                            totalItems={pagination.total}
                            itemsPerPage={pagination.perPage}
                            currentPage={pagination.currentPage}
                            from={pagination.from}
                            to={pagination.to}
                            onPageChange={(page) =>
                                setPagination(prev => ({
                                    ...prev,
                                    currentPage: page,
                                }))
                            }
                        />
                    )}
                </div>
            )}

            {/* ═══════════════════════════════════════════════════════════
          MODAL: Crear / Editar Sucursal
      ══════════════════════════════════════════════════════════════ */}
            <Dialog open={showModal} onOpenChange={setShowModal}>
                <DialogContent className="sm:max-w-md bg-popover">
                    <form onSubmit={handleSubmit(onSubmit)}>
                        <DialogHeader>
                            <DialogTitle className="flex items-center gap-2 text-lg font-semibold text-popover-foreground">
                                <Building2 className="h-5 w-5 text-primary" />
                                {editingId ? 'Editar Sucursal' : 'Nueva Sucursal'}
                            </DialogTitle>
                            <DialogDescription>
                                {editingId
                                    ? 'Modifica los datos de la sucursal existente.'
                                    : 'Ingresa los datos para registrar un nuevo punto de venta.'}
                            </DialogDescription>
                        </DialogHeader>

                        <div className="space-y-4 py-4">
                            <div className="space-y-2">
                                <label className="text-sm font-medium text-popover-foreground">
                                    Nombre de la sucursal <span className="text-destructive">*</span>
                                </label>
                                <Input
                                    placeholder="Ej. Sucursal Centro"
                                    {...register('name')}
                                />
                                <FormError message={errors.name?.message} />
                            </div>

                            <div className="space-y-2">
                                <label className="text-sm font-medium text-popover-foreground">
                                    Dirección
                                </label>
                                <Input
                                    placeholder="Calle, número, colonia..."
                                    {...register('address')}
                                />
                                <FormError message={errors.address?.message} />
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <label className="text-sm font-medium text-popover-foreground">
                                        Teléfono
                                    </label>
                                    <Input
                                        placeholder="Ej. 6671234567"
                                        {...register('phone_number')}
                                    />
                                    <FormError message={errors.phone_number?.message} />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-sm font-medium text-popover-foreground">
                                        Correo electrónico
                                    </label>
                                    <Input
                                        type="email"
                                        placeholder="contacto@sucursal.com"
                                        {...register('email')}
                                    />
                                    <FormError message={errors.email?.message} />
                                </div>
                            </div>
                            {/* Horarios */}
                            <div className="space-y-2">
                                <label className="text-sm font-medium text-popover-foreground">
                                    Horario de Lunes a Viernes
                                </label>

                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-1">
                                        <label className="text-xs text-muted-foreground">
                                            Apertura
                                        </label>
                                        <Input
                                            type="time"
                                            value={weekdayOpen}
                                            onChange={(e) => setWeekdayOpen(e.target.value)}
                                            
                                        />
                                    </div>

                                    <div className="space-y-1">
                                        <label className="text-xs text-muted-foreground">
                                            Cierre
                                        </label>
                                        <Input
                                            type="time"
                                            value={weekdayClose}
                                            onChange={(e) => setWeekdayClose(e.target.value)}
                                            
                                        />
                                    </div>
                                </div>

                                <FormError message={errors.weekday_hours?.message} />
                            </div>

                            <div className="space-y-2">
                                <label className="text-sm font-medium text-popover-foreground">
                                    Horario Sábados y Domingos
                                </label>

                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-1">
                                        <label className="text-xs text-muted-foreground">
                                            Apertura
                                        </label>
                                        <Input
                                            type="time"
                                            value={weekendOpen}
                                            onChange={(e) => setWeekendOpen(e.target.value)}
                                            disabled={saturdayClosed && sundayClosed}
                                            
                                        />
                                    </div>

                                    <div className="space-y-1">
                                        <label className="text-xs text-muted-foreground">
                                            Cierre
                                        </label>
                                        <Input
                                            type="time"
                                            value={weekendClose}
                                            onChange={(e) => setWeekendClose(e.target.value)}
                                            disabled={saturdayClosed && sundayClosed}
                                            
                                        />
                                    </div>
                                </div>

                                <FormError message={errors.weekend_hours?.message} />

                                <div className="flex items-center gap-6 pt-1">
                                    <label className="flex cursor-pointer items-center gap-2 text-sm text-popover-foreground">
                                        <input
                                            type="checkbox"
                                            {...register('saturday_closed')}
                                            className="h-4 w-4"
                                        />
                                        Sábado cerrado
                                    </label>

                                    <label className="flex cursor-pointer items-center gap-2 text-sm text-popover-foreground">
                                        <input
                                            type="checkbox"
                                            {...register('sunday_closed')}
                                            className="h-4 w-4"
                                        />
                                        Domingo cerrado
                                    </label>
                                </div>
                            </div>
                            <div className="flex items-center gap-2 pt-2">
                                <button
                                    type="button"
                                    role="switch"
                                    aria-checked={isActiveValue}
                                    onClick={() => setValue('is_active', !isActiveValue)}
                                    className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer items-center justify-center rounded-full transition-colors duration-200 ease-in-out focus:outline-none ${isActiveValue ? 'bg-primary' : 'bg-primary/20'
                                        }`}
                                >
                                    <span
                                        aria-hidden="true"
                                        className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-popover shadow ring-0 transition duration-200 ease-in-out ${isActiveValue ? 'translate-x-2' : '-translate-x-2'
                                            }`}
                                    />
                                </button>
                                <span className="text-sm text-popover-foreground">Sucursal Activa</span>
                            </div>
                        </div>

                        <DialogFooter className="gap-2 sm:justify-end">
                            <Button type="button" variant="outline" onClick={handleCloseModal} disabled={isSubmitting}>
                                Cancelar
                            </Button>
                            <Button
                                type="submit"
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? (
                                    <Spinner className="mr-2 h-4 w-4" />
                                ) : (
                                    <Check className="mr-2 h-4 w-4" />
                                )}
                                Guardar
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* ═══════════════════════════════════════════════════════════
          MODAL: Confirmar Eliminación
      ══════════════════════════════════════════════════════════════ */}
            <Dialog
                open={deleteConfirmId !== null}
                onOpenChange={(open) => !open && setDeleteConfirmId(null)}
            >
                <DialogContent className="sm:max-w-md bg-popover">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-lg font-semibold text-destructive">
                            <AlertCircle className="h-5 w-5" />
                            Eliminar Sucursal
                        </DialogTitle>
                        <DialogDescription className="pt-2">
                            ¿Estás seguro de que deseas eliminar esta sucursal? Esta acción no
                            se puede deshacer y podría afectar los registros asociados a ella.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter className="mt-4 gap-2 sm:justify-end">
                        <Button
                            variant="outline"
                            onClick={() => setDeleteConfirmId(null)}
                        >
                            Cancelar
                        </Button>
                        <Button
                            variant="destructive"
                            onClick={() => deleteConfirmId && handleDelete(deleteConfirmId)}
                        >
                            Sí, eliminar
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <ConfirmationModal
                isOpen={statusConfirmId !== null}
                onClose={() => setStatusConfirmId(null)}
                onConfirm={executeChangeStatus}
                preset="toggleStatus"
                itemName="sucursal"
            />

            {/* Modal Coberturas de Envío */}
            {shippingCoverageSucursal && (
                <ShippingCoverageModal
                    sucursal={shippingCoverageSucursal}
                    open={shippingCoverageSucursal !== null}
                    onClose={() => setShippingCoverageSucursal(null)}
                />
            )}
        </div>
    )
}
